import type { AuditEntry } from '@bb/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { btnSecondary, cardCls } from '@/lib/ui';

/**
 * What has happened to this account.
 *
 * The log has always been written and never shown, which was survivable while
 * an account was one person: there was only ever one person it could have been.
 * Once several people act in one workspace, "who removed Yuki" and "who moved
 * that booking" are questions somebody will ask, and the answer should not be a
 * database query.
 */
export const Route = createFileRoute('/app/settings/activity')({
  component: ActivityPage,
});

interface Page {
  items: AuditEntry[];
  nextCursor: string | null;
}

function ActivityPage() {
  const { t, i18n } = useTranslation(['account', 'common']);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery({
    queryKey: ['account', 'activity'],
    queryFn: ({ pageParam }) =>
      api<Page>(
        `/v1/account/activity${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last: Page) => last.nextCursor ?? undefined,
  });

  const entries = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm text-neutral-600">{t('account:activity.body')}</p>

      <section className={cardCls}>
        {isPending ? (
          <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-neutral-500">{t('account:activity.empty')}</p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {entries.map((e) => (
              <Row key={e.id} entry={e} locale={i18n.language} />
            ))}
          </ul>
        )}
        {hasNextPage ? (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className={`${btnSecondary} mt-4`}
          >
            {t('account:activity.more')}
          </button>
        ) : null}
      </section>
    </div>
  );
}

function Row({ entry, locale }: { entry: AuditEntry; locale: string }) {
  const { t } = useTranslation(['account']);
  const when = DateTime.fromISO(entry.at).setLocale(locale);
  /*
    An action with no translation still gets a line, showing its raw name.
    Dropping it would be worse: a gap in a history is a thing people notice and
    cannot explain, and the raw name is at least honest about what it is.
  */
  const label = t(`account:activity.actions.${entry.action}`, { defaultValue: entry.action });

  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
      <span className="text-sm">
        {label}
        {entry.detail ? <span className="text-neutral-500"> · {entry.detail}</span> : null}
      </span>
      <time
        dateTime={entry.at}
        title={when.toLocaleString(DateTime.DATETIME_FULL)}
        className="shrink-0 text-xs tabular-nums text-neutral-500"
      >
        {when.toLocaleString(DateTime.DATETIME_MED)}
      </time>
    </li>
  );
}
